import { cx } from "@emotion/css";
import { useFormik } from "formik";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { DeckFlashcard } from "src/api/flashcardDecks";
import {
	OutlinedTextBlackButton,
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FormTextField } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { PermissionsFilters } from "src/components/NewComponents/Filters/FlashcardsFilters/PermissionFilter";
import { DeckFlashcardsList } from "src/components/NewComponents/Flashcards/DeckFlashcardsList";
import { DeckLastChange } from "src/components/NewComponents/Flashcards/DeckLastChange";
import { EditableFlashcardCard } from "src/components/NewComponents/Flashcards/EditableFlashcardCard";
import {
	useGetUserDeck,
	useRemoveFlashcard,
	useUpdateDeck,
	useUpdateFlashcard,
} from "src/hooks/useFlashcards";
import { useFontSize } from "src/hooks/useFontSize";
import FlashcardIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";
import * as Yup from "yup";

import {
	Modal,
	ModalHeader,
	ModalStickyFooter,
} from "./Components/ModalsComponents";

interface EditDeckModalProps {
	deckId: number;
	deckName: string;
	deckCode: string;
	onClose: () => void;
}

interface EditableFlashcard extends DeckFlashcard {
	flashcardRelatedId: number;
}

const TABS = {
	GENERAL: 0,
	FLASHCARDS: 1,
} as const;

// Modal stack renderuje tylko najwyższy modal, więc otwarcie modala dodawania
// fiszki odmontowuje EditDeckModal. Zapamiętujemy aktywny tab per zestaw, żeby
// po powrocie wylądować na tym samym tabie (a nie zawsze na "Ogólne").
const lastActiveTabByDeck: Record<string, number> = {};

export function EditDeckModal({
	deckId,
	deckName,
	deckCode,
	onClose,
}: EditDeckModalProps) {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const { modalHeaderFontSizeClass, fontSizeClass } = useFontSize();

	const { data, isLoading } = useGetUserDeck(deckCode);
	const { mutateAsync: updateDeck } = useUpdateDeck();
	const { mutateAsync: removeFlashcard } = useRemoveFlashcard(deckCode);
	const { mutateAsync: updateFlashcard, isPending: isUpdatingFlashcard } =
		useUpdateFlashcard();

	const [selectedTab, setSelectedTab] = useState<number>(
		lastActiveTabByDeck[deckCode] ?? TABS.GENERAL,
	);

	const handleTabChange = (value: number) => {
		lastActiveTabByDeck[deckCode] = value;
		setSelectedTab(value);
	};

	const handleClose = () => {
		delete lastActiveTabByDeck[deckCode];
		onClose();
	};

	const [editingId, setEditingId] = useState<number | null>(null);

	const generalForm = useFormik<{
		name: string;
		description: string;
		permission: string;
	}>({
		enableReinitialize: true,
		initialValues: {
			name: data?.name ?? deckName,
			description: data?.description ?? "",
			permission: data?.permission ?? "public",
		},
		validationSchema: Yup.object().shape({
			name: Yup.string().trim().required(t("errors.emptySingleField")),
		}),
		onSubmit: async (values) => {
			try {
				await updateDeck({
					code: deckCode,
					name: values.name.trim(),
					description: values.description.trim(),
					permission: values.permission,
				});
				toast.success(t("commands.saved", "Zapisano"), {
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 3000,
				});
				handleClose();
			} catch {
				toast.error(t("errors.somethingWentWrong", "Coś poszło nie tak"), {
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 3000,
				});
			}
		},
	});

	const flashcards = useMemo<EditableFlashcard[]>(() => {
		const relations = (data?.flashcards ?? []) as Array<{
			id: number;
			flashcard: DeckFlashcard;
		}>;
		return relations
			.filter((rel) => rel?.flashcard)
			.map((rel) => ({
				...rel.flashcard,
				flashcardRelatedId: rel.id,
			}));
	}, [data]);

	const handleSaveFlashcard = async (
		flashcard: EditableFlashcard,
		next: { front: string; back: string },
	) => {
		try {
			await updateFlashcard({
				id: flashcard.id,
				deckId,
				front: next.front,
				back: next.back,
				code: deckCode,
			});
			setEditingId(null);
		} catch {
			toast.error(t("errors.somethingWentWrong", "Coś poszło nie tak"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
		}
	};

	const handleOpenAddFlashcard = () => {
		openModal(ModalsNames.AddFlashcardModal, { deckCode });
	};

	const handleDelete = (flashcard: EditableFlashcard) => {
		const wasLastFlashcard = flashcards.length <= 1;
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.flashcard.title", "Usunąć fiszkę?"),
			description: (
				<>
					{t(
						"modals.deleteItem.flashcard.description",
						"Fiszka zostanie trwale usunięta. Tej operacji nie można cofnąć.",
					)}
					{wasLastFlashcard && (
						<strong className="text-error">
							{" "}
							{t(
								"modals.deleteItem.flashcard.lastWarning",
								"To jest ostatnia fiszka w zestawie — usunięcie jej spowoduje usunięcie całego zestawu.",
							)}
						</strong>
					)}
				</>
			),
			confirmText: t("modals.deleteItem.flashcard.confirm", "Usuń fiszkę"),
			onDelete: async () => {
				await removeFlashcard({ deckId, flashcardId: flashcard.id });
				if (wasLastFlashcard) {
					handleClose();
				}
			},
		});
	};

	const tabs = [
		t("modals.editDeck.tabs.general", "Ogólne"),
		t("modals.editDeck.tabs.flashcards", "Fiszki"),
	];

	const renderGeneralTab = () => (
		<div className="flex flex-col gap-24 mt-24">
			<div className="flex flex-col gap-8">
				<span className={cx(modalHeaderFontSizeClass, "text-dark-grey")}>
					{t("modals.editDeck.permissionLabel", "Dostępność zestawu")}
				</span>
				<PermissionsFilters
					permission={generalForm.values.permission}
					setPermission={(value) =>
						generalForm.setFieldValue("permission", value)
					}
				/>
			</div>
			<div className="flex flex-col gap-8">
				<label
					htmlFor="edit-deck-name"
					className={cx(modalHeaderFontSizeClass, "text-dark-grey")}
				>
					{t("modals.editDeck.nameLabel", "Nazwa zestawu")}
				</label>
				<FormTextField
					id="edit-deck-name"
					fullWidth
					value={generalForm.values.name}
					error={generalForm.errors.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						generalForm.setFieldValue("name", e.target.value)
					}
					slotProps={{ htmlInput: { "data-test-id": "edit-deck-name-input" } }}
				/>
			</div>
			<div className="flex flex-col gap-8">
				<div className="flex items-center justify-between gap-8">
					<label
						htmlFor="edit-deck-description"
						className={cx(modalHeaderFontSizeClass, "text-dark-grey")}
					>
						{t("modals.editDeck.descriptionLabel", "Opis zestawu")}
					</label>
					<span className={cx(fontSizeClass, "text-grey")}>
						{t("modals.editDeck.optional", "opcjonalnie")}
					</span>
				</div>
				<FormTextField
					id="edit-deck-description"
					className="text-field--form-multiline"
					fullWidth
					multiline
					minRows={6}
					placeholder={t(
						"modals.editDeck.descriptionPlaceholder",
						"Np. najważniejsze pojęcia z ortopedii dziecięcej do LEK",
					)}
					value={generalForm.values.description}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
						generalForm.setFieldValue("description", e.target.value)
					}
					slotProps={{
						htmlInput: { "data-test-id": "edit-deck-description-input" },
					}}
				/>
			</div>
		</div>
	);

	const renderAddFlashcardButton = () => (
		<OutlinedTextBlackButton
			className="flex items-center gap-4"
			onClick={handleOpenAddFlashcard}
			data-test-id="edit-deck-add-flashcard-button"
		>
			<Plus className="icon-super-small" />
			<span>{t("menuActions.flashcards.addFlashcard", "Dodaj fiszkę")}</span>
		</OutlinedTextBlackButton>
	);

	const renderFlashcardsTab = () => (
		<DeckFlashcardsList
			flashcards={flashcards}
			isLoading={isLoading}
			searchTestId="edit-deck-search-input"
			renderItem={(flashcard, query) => (
				<EditableFlashcardCard
					key={flashcard.flashcardRelatedId}
					flashcard={flashcard}
					query={query}
					isEditing={editingId === flashcard.flashcardRelatedId}
					onStartEdit={() => setEditingId(flashcard.flashcardRelatedId)}
					onCancelEdit={() => setEditingId(null)}
					onSave={(next) => handleSaveFlashcard(flashcard, next)}
					onDelete={() => handleDelete(flashcard)}
					isSaving={isUpdatingFlashcard}
					testId={`edit-deck-flashcard-${flashcard.flashcardRelatedId}`}
				/>
			)}
		/>
	);

	return (
		<Modal onClose={handleClose} maxWidth="md">
			<ModalHeader
				Icon={FlashcardIcon}
				title={t("modals.editDeck.title", "Edytuj zestaw")}
				hasCloseButton
			/>
			<Box
				className="border-none box-shadow-none p-0 mt-24"
				headerClassName="mb-0 border-bottom-1 pb-12"
				tabs={tabs}
				selectedTab={selectedTab}
				onTabsChange={handleTabChange}
				tabTestIds={["edit-deck-tab-general", "edit-deck-tab-flashcards"]}
				additionalTabsActions={
					selectedTab === TABS.FLASHCARDS ? renderAddFlashcardButton : undefined
				}
			/>

			{selectedTab === TABS.GENERAL
				? renderGeneralTab()
				: renderFlashcardsTab()}

			{selectedTab === TABS.GENERAL && (
				<ModalStickyFooter className="justify-between gap-12 mt-24 pt-16 border-top-1">
					<DeckLastChange
						dates={[data?.updatedAt, ...flashcards.map((f) => f.updatedAt)]}
					/>
					<div className="flex items-center gap-12">
						<TextPrimaryButton
							onClick={handleClose}
							disabled={generalForm.isSubmitting}
							data-test-id="edit-deck-cancel-general-button"
						>
							{t("commands.cancel", "Anuluj")}
						</TextPrimaryButton>
						<PrimaryButton
							onClick={() => generalForm.handleSubmit()}
							disabled={
								generalForm.isSubmitting || !generalForm.values.name.trim()
							}
							data-test-id="edit-deck-save-general-button"
						>
							{t("commands.save", "Zapisz")}
						</PrimaryButton>
					</div>
				</ModalStickyFooter>
			)}
		</Modal>
	);
}
